import { CalendarRange, ChevronRight, Crown, Filter, LockKeyhole, Search, Shield, Trophy, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { ClubBadge } from '../components/ClubBadge';
import { TeamBadge } from '../components/TeamBadge';
import { TeamProfileModal } from '../components/TeamProfileModal';
import { canFieldRivalTeam, createDoubleRoundRobinSchedule, createLeagueGroups, gameSeasons, rivalTeams, type RivalTeam } from '../competition';
import type { GameState } from '../gameState';

type RivalView = 'seasons' | 'groups' | 'champions';

export function RivalsPage({ game }: { game: GameState }) {
  const [view, setView] = useState<RivalView>(() => {
    const requested = new URLSearchParams(window.location.search).get('rivalView');
    return requested === 'groups' || requested === 'champions' ? requested : 'seasons';
  });
  const [gameSeason, setGameSeason] = useState(gameSeasons[0] ?? '');
  const [groupIndex, setGroupIndex] = useState(0);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<RivalTeam | null>(() => {
    const requested = new URLSearchParams(window.location.search).get('team')?.toLocaleLowerCase();
    return requested ? rivalTeams.find((team) => team.id.toLocaleLowerCase() === requested || team.name.toLocaleLowerCase() === requested) ?? null : null;
  });
  const groups = useMemo(() => createLeagueGroups(game.save.leagueSeed, game.save.clubName), [game.save.clubName, game.save.leagueSeed]);
  const selectedGroup = groups[groupIndex];
  const schedule = useMemo(() => createDoubleRoundRobinSchedule(selectedGroup.teams.map((team) => team.id)), [selectedGroup]);

  const visibleTeams = useMemo(() => {
    const source = view === 'groups'
      ? selectedGroup.teams
      : rivalTeams.filter((team) => canFieldRivalTeam(team) && (!gameSeason || team.games.includes(gameSeason)));
    return source.filter((team) => team.name.toLowerCase().includes(query.toLowerCase()));
  }, [gameSeason, query, selectedGroup, view]);

  return <div className="page rivals-page">
    <header className="page-heading compact-heading">
      <div><span className="eyebrow">BASE DE DATOS</span><h1>Equipos</h1><p>Explora equipos históricos, consulta sus plantillas y descubre posibles oponentes.</p></div>
      <div className="rival-count"><Shield size={18} /><span><small>EQUIPOS REGISTRADOS</small><b>{rivalTeams.length}</b></span></div>
    </header>

    <div className="rival-tabs card">
      <button className={view === 'seasons' ? 'active' : ''} onClick={() => { setView('seasons'); setSelected(null); }}><CalendarRange size={17} /><span><b>Temporadas</b><small>Universo Inazuma Eleven</small></span></button>
      <button className={view === 'groups' ? 'active' : ''} onClick={() => { setView('groups'); setSelected(null); }}><UsersRound size={17} /><span><b>Grupos de liga</b><small>Temporada actual</small></span></button>
      <button className={view === 'champions' ? 'active' : ''} onClick={() => { setView('champions'); setSelected(null); }}><Trophy size={17} /><span><b>Torneo Champions</b><small>Desde temporada 2</small></span></button>
    </div>

    {view !== 'champions' && <div className="rival-toolbar card">
      <label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar equipo…" /></label>
      <span className="toolbar-separator" />
      <Filter size={15} />
      {view === 'seasons' ? <select value={gameSeason} onChange={(event) => { setGameSeason(event.target.value); setSelected(null); }}>{gameSeasons.map((season) => <option value={season} key={season}>{season}</option>)}</select>
        : <select value={groupIndex} onChange={(event) => { setGroupIndex(Number(event.target.value)); setSelected(null); }}>{groups.map((group, index) => <option value={index} key={group.id}>{group.name} · {group.teams.length} equipos</option>)}</select>}
      <span className="result-count">{visibleTeams.length} equipos</span>
    </div>}

    {view === 'champions'
      ? <ChampionsPanel season={game.save.season} />
      : <>
        <div className="rivals-workspace">
        <section>
          {view === 'groups' && <div className="group-summary card"><div><span className="eyebrow">TEMPORADA {game.save.season}</span><h2>{selectedGroup.name}</h2><p>{selectedGroup.teams.length} equipos · {schedule.length} jornadas · Ida y vuelta</p></div><div className="group-format"><span><b>2</b><small>CLASIFICAN</small></span><ChevronRight size={18} /><Trophy size={22} /></div></div>}
          <div className="rival-grid">
            {visibleTeams.map((team, index) => <TeamCard team={team} clubSave={game.save} rank={view === 'groups' ? index + 1 : null} selected={selected?.id === team.id} onClick={() => setSelected(team)} key={team.id} />)}
          </div>
        </section>
        </div>
        {selected && <TeamProfileModal team={selected} groupName={groups.find((group) => group.teams.some((candidate) => candidate.id === selected.id))?.name} season={game.save.season} save={game.save} onClose={() => setSelected(null)} />}
      </>}
  </div>;
}

function TeamCard({ team, clubSave, rank, selected, onClick }: { team: RivalTeam; clubSave: GameState['save']; rank: number | null; selected: boolean; onClick: () => void }) {
  return <button className={`rival-card card ${selected ? 'selected' : ''} ${team.id === 'team_aurora' ? 'user-club' : ''}`} onClick={onClick}>
    {rank && <span className="rival-rank">{rank}</span>}
    {team.id === 'team_aurora' ? <ClubBadge save={clubSave} size="md" /> : <TeamBadge name={team.name} imageUrl={team.image_url} size="md" />}
    <span className="rival-card-copy"><b>{team.name}</b><small>{team.games[0]}</small></span>
    <span className="rival-card-meta"><span><b>{team.players.length || 11}</b><small>JUGADORES</small></span><span><b>{team.rating}</b><small>MEDIA</small></span></span>
    <span className="face-stack">{team.players.slice(0, 3).map((player) => <PlayerAvatar player={player} size="md" key={player.id} />)}</span>
    <ChevronRight className="rival-chevron" size={16} />
  </button>;
}

function ChampionsPanel({ season }: { season: number }) {
  const locked = season < 2;
  return <section className="champions-panel card">
    <div className="champions-glow" />
    <span className="champions-icon">{locked ? <LockKeyhole size={28} /> : <Crown size={28} />}</span>
    <span className="eyebrow">TORNEO ESPECIAL</span><h2>{locked ? 'Disponible desde la temporada 2' : 'Torneo Champions'}</h2>
    <p>Los dos mejores equipos de cada uno de los 16 grupos se enfrentan en una eliminatoria de 32 participantes.</p>
    <div className="champions-format"><span><b>32</b><small>EQUIPOS</small></span><i /><span><b>5</b><small>RONDAS</small></span><i /><span><b>1</b><small>CAMPEÓN</small></span></div>
    <div className="champions-reward"><Crown size={21} /><span><small>RECOMPENSA DEL CAMPEÓN</small><b>Elegir directamente un jugador participante</b><p>El jugador se incorpora como copia coleccionable; su equipo de origen conserva la plantilla original.</p></span></div>
  </section>;
}
